import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import api from '../utils/api';
import theme from './theme';
import {
  HOSPITAL_ROLES,
  hospitalRoleLabel,
  can,
} from '../utils/userDashboardPermissions';
import { getUserRole } from '../utils/sessionStore';

const COLORS = theme.colors;

/**
 * Hospital ADMIN: invite and assign UserDashboard personas.
 * Dedicated EVS tablet / OPERATOR apps remain out of scope — members use web stubs.
 */
export default function MembersPage() {
  const { adminView, personaRole } = useOutletContext() || {};
  const role = personaRole || getUserRole();
  const allowed = !adminView && can(role, 'members:write');

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    password: '',
    role: 'IPC_LEAD',
  });

  const load = useCallback(async () => {
    if (!allowed) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/client/members');
      if (res.data?.success) setMembers(res.data.members || []);
      else setError(res.data?.message || 'Unable to load members');
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [allowed]);

  useEffect(() => {
    load();
  }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await api.post('/client/members', form);
      if (res.data?.success) {
        setForm({ name: '', email: '', phoneNumber: '', password: '', role: 'IPC_LEAD' });
        await load();
      } else {
        setError(res.data?.message || 'Invite failed');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Invite failed');
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (memberId, nextRole) => {
    setError('');
    try {
      const res = await api.patch(`/client/members/${memberId}`, { role: nextRole });
      if (res.data?.success) await load();
      else setError(res.data?.message || 'Update failed');
    } catch (err) {
      setError(err?.response?.data?.message || 'Update failed');
    }
  };

  if (!allowed) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        Only hospital ADMIN can manage members.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 960, mx: 'auto' }}>
      <Typography sx={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', mb: 0.5 }}>
        Members
      </Typography>
      <Typography sx={{ fontSize: 14, color: COLORS.textSecondary, mb: 3, maxWidth: 640 }}>
        Assign hospital personas for the UserDashboard. Housekeeping tablet (offline / PIN) and deep
        operator cycle tools remain separate clients — members here use the web portal stubs.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <Paper
        component="form"
        onSubmit={invite}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: `${theme.radius.lg}px`,
          border: `1px solid ${COLORS.borderDefault}`,
        }}
      >
        <Typography sx={{ fontWeight: 700, mb: 2 }}>Invite member</Typography>
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          <TextField
            label="Name"
            required
            size="small"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <TextField
            label="Email"
            type="email"
            required
            size="small"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
          <TextField
            label="Phone"
            required
            size="small"
            value={form.phoneNumber}
            onChange={(e) => setForm((f) => ({ ...f, phoneNumber: e.target.value }))}
          />
          <TextField
            label="Temporary password"
            type="password"
            required
            size="small"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          />
          <TextField
            select
            label="Role"
            size="small"
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
          >
            {HOSPITAL_ROLES.map((r) => (
              <MenuItem key={r} value={r}>
                {hospitalRoleLabel(r)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Button
          type="submit"
          variant="contained"
          disabled={saving}
          sx={{ mt: 2, textTransform: 'none', fontWeight: 600 }}
        >
          {saving ? 'Inviting…' : 'Invite'}
        </Button>
      </Paper>

      <Paper
        sx={{
          borderRadius: `${theme.radius.lg}px`,
          border: `1px solid ${COLORS.borderDefault}`,
          overflow: 'auto',
        }}
      >
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{m.name}</TableCell>
                  <TableCell>{m.email}</TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={m.role}
                      onChange={(e) => changeRole(m.id, e.target.value)}
                      sx={{ minWidth: 180 }}
                    >
                      {HOSPITAL_ROLES.map((r) => (
                        <MenuItem key={r} value={r}>
                          {hospitalRoleLabel(r)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    {m.isPrimary ? (
                      <Chip size="small" label="Primary" color="primary" variant="outlined" />
                    ) : (
                      <Chip size="small" label="Member" variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {members.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography sx={{ color: COLORS.textSecondary, py: 2 }}>
                      No members yet — invite your IPC lead or EVS supervisor.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}
